import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    Chip,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Pagination,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Stack,
    Tooltip,
    Tabs,
    Tab
} from '@mui/material';
import {
    Search as SearchIcon,
    PlayArrow as PlayIcon,
    OpenInNew as OpenInNewIcon,
    Business as BusinessIcon,
    TrendingUp as TrendingUpIcon,
    Assessment as AssessmentIcon,
    FilterList as FilterIcon,
    YouTube as YouTubeIcon,
    Instagram as InstagramIcon,
    Facebook as FacebookIcon,
    Podcasts as PodcastIcon
} from '@mui/icons-material';
import TikTokIcon from '../components/icons/TikTokIcon';
import apiService from '../services/api';
import { formatChileanDate } from '../utils/dateUtils';
import { slugify } from '../utils/textUtils';

interface Brand {
    _id: string;
    name: string;
    aliases: string[];
    antiAliases?: string[];
    category?: string;
    isActive: boolean;
    canSponsor: boolean;
    sponsorshipNotes?: string;
    createdAt: string;
    updatedAt: string;
    lastMentionAt?: string;
}

interface BrandMention {
    _id: string;
    contentId: string;
    brandId?: string;
    rawMentionName?: string;
    brandName: string;
    startTime: number;
    endTime: number;
    duration: number;
    context: string;
    confidence: number;
    category?: string;
    classification: 'paid' | 'organic' | 'unknown';
    fragmentIndex?: number;
    createdAt: string;
    updatedAt: string;
    content?: {
        _id: string;
        title: string;
        url?: string;
        publishedAt: string;
        source: string;
        mediaType: string;
        thumbnail?: string;
        duration?: number;
        podcast?: {
            _id: string;
            title: string;
            imageUrl?: string;
        };
    };
}



interface Podcast {
    _id: string;
    title: string;
    slug?: string;
    imageUrl?: string;
    author?: string;
    description?: string;
    category?: 'politics' | 'other';
    type?: 'tv' | 'noticias' | 'radio' | 'digital';
    language?: string;
    lastUpdated?: string;
    episodeCount?: number;
    createdAt?: string;
}

const BrandExplorer: React.FC = () => {
    const navigate = useNavigate();

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Brand-related state
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
    const [mentions, setMentions] = useState<BrandMention[]>([]);
    const [mentionsLoading, setMentionsLoading] = useState(false);

    // Podcast-related state
    const [selectedPodcast, setSelectedPodcast] = useState<Podcast | null>(null);

    // Brand filters and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [podcastFilter, setPodcastFilter] = useState('');
    const [page, setPage] = useState(1);
    const [mentionsPage, setMentionsPage] = useState(1);
    const [totalMentionsPages, setTotalMentionsPages] = useState(1);

    // Podcast filters and pagination
    const [podcastSearchTerm, setPodcastSearchTerm] = useState('');
    const [podcastCategoryFilter, setPodcastCategoryFilter] = useState('');
    const [podcastSortBy, setPodcastSortBy] = useState('');
    const [podcastPage, setPodcastPage] = useState(1);

    // Dialog state
    const [mentionDialogOpen, setMentionDialogOpen] = useState(false);
    const [selectedMention, setSelectedMention] = useState<BrandMention | null>(null);

    // Query for brands
    const {
        data: brandsData,
        isLoading: loading,
        error: brandsError,
    } = useQuery({
        queryKey: ['brands', page, searchTerm, categoryFilter],
        queryFn: async () => {
            // Always filter to only sponsoring brands
            const response = await apiService.getBrands(
                page,
                20,
                searchTerm || undefined,
                categoryFilter || undefined,
                true // Always true for canSponsor
            );
            return response;
        },
        enabled: activeTab === 0, // Only fetch when brands tab is active
        staleTime: 2 * 60 * 1000, // 2 minutes
    });

    // Extract brands and pagination from query data
    const brands = brandsData?.brands || [];
    const totalPages = brandsData?.pagination?.pages || 1;
    const error = brandsError ? 'Failed to load brands' : null;

    const loadBrandMentions = useCallback(async () => {
        if (!selectedBrand) return;

        try {
            setMentionsLoading(true);
            const response = await apiService.getBrandMentions(
                selectedBrand._id,
                mentionsPage,
                10,
                podcastFilter || undefined
            );
            setMentions(response.mentions);
            setTotalMentionsPages(response.pagination.pages);
        } catch (err) {
            console.error('Error loading brand mentions:', err);
        } finally {
            setMentionsLoading(false);
        }
    }, [selectedBrand, mentionsPage, podcastFilter]);

    // Load brand mentions when a brand is selected
    useEffect(() => {
        if (selectedBrand) {
            loadBrandMentions();
        }
    }, [selectedBrand, mentionsPage, loadBrandMentions]);

    // Query for brand stats
    const { data: stats } = useQuery({
        queryKey: ['brand-stats'],
        queryFn: async () => {
            const response = await apiService.getBrandStats();
            return response;
        },
        enabled: activeTab === 0, // Only fetch when brands tab is active
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Query for podcast filter options (for brands section)
    const { data: podcastFilterOptions = [] } = useQuery({
        queryKey: ['brand-podcasts'],
        queryFn: async () => {
            const response = await apiService.getBrandPodcasts();
            return response.podcasts || [];
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Query for main podcasts list (for podcasts section)
    const {
        data: podcastsData,
        isLoading: podcastsLoading,
        error: podcastsQueryError,
    } = useQuery({
        queryKey: ['podcasts', podcastPage, podcastSearchTerm, podcastCategoryFilter, podcastSortBy],
        queryFn: async () => {
            const response = await apiService.getPodcasts(
                podcastPage,
                20,
                podcastSearchTerm || undefined,
                podcastCategoryFilter || undefined,
                podcastSortBy || undefined
            );
            return response;
        },
        enabled: activeTab === 1, // Only fetch when podcasts tab is active
        staleTime: 2 * 60 * 1000, // 2 minutes
    });

    // Extract podcasts and pagination from query data
    const podcasts = podcastsData?.podcasts || [];
    const totalPodcastPages = podcastsData?.pagination?.pages || 1;
    const podcastsError = podcastsQueryError ? 'Failed to load podcasts' : null;

    const handleBrandSelect = (brand: Brand) => {
        setSelectedBrand(brand);
        setMentionsPage(1);
        setMentions([]);
        setPodcastFilter(''); // Reset podcast filter when selecting a new brand
    };

    const handlePodcastSelect = (podcast: Podcast) => {
        // Navigate to podcast brand mentions page
        const podcastSlug = podcast.slug || slugify(podcast.title);
        navigate(`/explorar/brands/podcast/${podcastSlug}`);
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        // Reset selections when switching tabs
        if (newValue === 0) {
            setSelectedPodcast(null);
        } else {
            setSelectedBrand(null);
            setMentions([]);
        }
    };

    const handleMentionClick = (mention: BrandMention) => {
        setSelectedMention(mention);
        setMentionDialogOpen(true);
    };

    const createYouTubeUrlWithTimestamp = (url: string, startTime: number): string => {
        if (!url) return '';

        const urlObj = new URL(url);
        urlObj.searchParams.set('t', Math.floor(startTime).toString() + 's');
        return urlObj.toString();
    };

    const getSourceIcon = (source: string) => {
        switch (source) {
            case 'youtube':
                return <YouTubeIcon sx={{ color: '#FF0000' }} />;
            case 'instagram':
                return <InstagramIcon sx={{ color: '#E4405F' }} />;
            case 'tiktok':
                return <TikTokIcon sx={{ color: '#000000' }} />;
            case 'facebook':
                return <FacebookIcon sx={{ color: '#1877F2' }} />;
            default:
                return <BusinessIcon />;
        }
    };

    const getClassificationColor = (classification: string) => {
        switch (classification) {
            case 'paid':
                return 'success';
            case 'organic':
                return 'info';
            default:
                return 'default';
        }
    };

    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    if (loading && brands.length === 0 && activeTab === 0) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                🏷️ Brand Explorer
            </Typography>

            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Explore brand mentions across podcast content with detailed analytics and direct YouTube navigation
            </Typography>

            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="brand explorer tabs">
                    <Tab
                        icon={<BusinessIcon />}
                        label="All Brands"
                        iconPosition="start"
                        sx={{ textTransform: 'none' }}
                    />
                    <Tab
                        icon={<PodcastIcon />}
                        label="All Podcasts"
                        iconPosition="start"
                        sx={{ textTransform: 'none' }}
                    />
                </Tabs>
            </Box>

            {/* Brands Tab Content */}
            {activeTab === 0 && (
                <>
                    {/* Stats Cards */}
                    {stats && (
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Card>
                                    <CardContent>
                                        <Box display="flex" alignItems="center">
                                            <BusinessIcon color="primary" sx={{ mr: 1 }} />
                                            <Box>
                                                <Typography variant="h6">{stats.totalBrands}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Brands
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Card>
                                    <CardContent>
                                        <Box display="flex" alignItems="center">
                                            <AssessmentIcon color="secondary" sx={{ mr: 1 }} />
                                            <Box>
                                                <Typography variant="h6">{stats.totalMentions}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Mentions
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Card>
                                    <CardContent>
                                        <Box display="flex" alignItems="center">
                                            <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                                            <Box>
                                                <Typography variant="h6">{stats.sponsoringBrands}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Sponsoring Brands
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Card>
                                    <CardContent>
                                        <Box display="flex" alignItems="center">
                                            <FilterIcon color="warning" sx={{ mr: 1 }} />
                                            <Box>
                                                <Typography variant="h6">{stats.nonSponsoringBrands}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Non-Sponsoring
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    <Grid container spacing={3}>
                        {/* Left Panel - Brand List */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Brands
                                    </Typography>

                                    {/* Filters */}
                                    <Stack spacing={2} sx={{ mb: 3 }}>
                                        <TextField
                                            fullWidth
                                            placeholder="Search brands..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setPage(1);
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        <Grid container spacing={2}>
                                            <Grid size={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Category</InputLabel>
                                                    <Select
                                                        value={categoryFilter}
                                                        label="Category"
                                                        onChange={(e) => {
                                                            setCategoryFilter(e.target.value);
                                                            setPage(1);
                                                        }}
                                                    >
                                                        <MenuItem value="">All Categories</MenuItem>
                                                        <MenuItem value="food_beverage">Food & Beverage</MenuItem>
                                                        <MenuItem value="technology">Technology</MenuItem>
                                                        <MenuItem value="retail">Retail</MenuItem>
                                                        <MenuItem value="services">Services</MenuItem>
                                                        <MenuItem value="entertainment">Entertainment</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid size={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Podcast</InputLabel>
                                                    <Select
                                                        value={podcastFilter}
                                                        label="Podcast"
                                                        onChange={(e) => {
                                                            setPodcastFilter(e.target.value);
                                                            setMentionsPage(1);
                                                        }}
                                                    >
                                                        <MenuItem value="">All Podcasts</MenuItem>
                                                        {podcastFilterOptions.map((podcast) => (
                                                            <MenuItem key={podcast._id} value={podcast._id}>
                                                                {podcast.title}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Stack>

                                    {/* Brand List */}
                                    {loading ? (
                                        <Box display="flex" justifyContent="center" p={3}>
                                            <CircularProgress />
                                        </Box>
                                    ) : error ? (
                                        <Alert severity="error">{error}</Alert>
                                    ) : (
                                        <>
                                            <List>
                                                {brands.map((brand, index) => (
                                                    <React.Fragment key={brand._id}>
                                                        <ListItem
                                                            onClick={() => handleBrandSelect(brand)}
                                                            sx={{
                                                                borderRadius: 1,
                                                                mb: 1,
                                                                cursor: 'pointer',
                                                                backgroundColor: selectedBrand?._id === brand._id ? 'primary.light' : 'transparent',
                                                                color: selectedBrand?._id === brand._id ? 'primary.contrastText' : 'inherit',
                                                                '&:hover': {
                                                                    backgroundColor: selectedBrand?._id === brand._id ? 'primary.main' : 'action.hover',
                                                                },
                                                            }}
                                                        >
                                                            <ListItemAvatar>
                                                                <Avatar sx={{ bgcolor: 'success.main' }}>
                                                                    <BusinessIcon />
                                                                </Avatar>
                                                            </ListItemAvatar>
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="subtitle1">{brand.name}</Typography>
                                                                }
                                                                secondary={
                                                                    <Box>
                                                                        {brand.category && (
                                                                            <Chip label={brand.category} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                }
                                                            />
                                                        </ListItem>
                                                        {index < brands.length - 1 && <Divider />}
                                                    </React.Fragment>
                                                ))}
                                            </List>

                                            {/* Pagination */}
                                            {totalPages > 1 && (
                                                <Box display="flex" justifyContent="center" mt={2}>
                                                    <Pagination
                                                        count={totalPages}
                                                        page={page}
                                                        onChange={(_, newPage) => setPage(newPage)}
                                                        color="primary"
                                                    />
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Right Panel - Brand Mentions */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {selectedBrand ? `${selectedBrand.name} - Mentions` : 'Select a Brand'}
                                    </Typography>

                                    {selectedBrand ? (
                                        <>
                                            {/* Brand Info */}
                                            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                                                <Grid container spacing={2}>
                                                    <Grid size={12}>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Brand Details
                                                        </Typography>
                                                        <Typography variant="body1" fontWeight="bold">
                                                            {selectedBrand.name}
                                                        </Typography>
                                                        {selectedBrand.aliases.length > 0 && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Aliases: {selectedBrand.aliases.join(', ')}
                                                            </Typography>
                                                        )}
                                                        <Box mt={1}>
                                                            <Chip
                                                                label="Sponsoring Brand"
                                                                color="success"
                                                                size="small"
                                                            />
                                                            {selectedBrand.category && (
                                                                <Chip
                                                                    label={selectedBrand.category}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    sx={{ ml: 1 }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Paper>

                                            {/* Mentions List */}
                                            {mentionsLoading ? (
                                                <Box display="flex" justifyContent="center" p={3}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : mentions.length === 0 ? (
                                                <Alert severity="info">No mentions found for this brand</Alert>
                                            ) : (
                                                <>
                                                    <List>
                                                        {mentions.map((mention, index) => (
                                                            <React.Fragment key={mention._id}>
                                                                <ListItem
                                                                    onClick={() => handleMentionClick(mention)}
                                                                    sx={{
                                                                        borderRadius: 1,
                                                                        mb: 1,
                                                                        cursor: 'pointer',
                                                                        '&:hover': {
                                                                            backgroundColor: 'action.hover',
                                                                        },
                                                                    }}
                                                                >
                                                                    <ListItemAvatar>
                                                                        <Avatar>
                                                                            {mention.content && getSourceIcon(mention.content.source)}
                                                                        </Avatar>
                                                                    </ListItemAvatar>
                                                                    <ListItemText
                                                                        primary={
                                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                                <Typography variant="subtitle2" noWrap>
                                                                                    {mention.content?.title || 'Unknown Content'}
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={mention.classification}
                                                                                    size="small"
                                                                                    color={getClassificationColor(mention.classification) as any}
                                                                                />
                                                                            </Box>
                                                                        }
                                                                        secondary={
                                                                            <Box>
                                                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                                                    {mention.context.substring(0, 100)}...
                                                                                </Typography>
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {formatTime(mention.startTime)} - {formatTime(mention.endTime)} •
                                                                                    {(mention.confidence * 100).toFixed(0)}% confidence
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                    />
                                                                    {mention.content?.url && (
                                                                        <Tooltip title="Open in YouTube">
                                                                            <IconButton
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    const url = createYouTubeUrlWithTimestamp(
                                                                                        mention.content!.url!,
                                                                                        mention.startTime
                                                                                    );
                                                                                    window.open(url, '_blank');
                                                                                }}
                                                                                size="small"
                                                                            >
                                                                                <OpenInNewIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </ListItem>
                                                                {index < mentions.length - 1 && <Divider />}
                                                            </React.Fragment>
                                                        ))}
                                                    </List>

                                                    {/* Mentions Pagination */}
                                                    {totalMentionsPages > 1 && (
                                                        <Box display="flex" justifyContent="center" mt={2}>
                                                            <Pagination
                                                                count={totalMentionsPages}
                                                                page={mentionsPage}
                                                                onChange={(_, newPage) => setMentionsPage(newPage)}
                                                                color="primary"
                                                            />
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <Alert severity="info">
                                            Select a brand from the left panel to view its mentions
                                        </Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Podcasts Tab Content */}
            {activeTab === 1 && (
                <Grid container spacing={3}>
                    {/* Left Panel - Podcast List */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Podcasts
                                </Typography>

                                {/* Podcast Filters */}
                                <Stack spacing={2} sx={{ mb: 3 }}>
                                    <TextField
                                        fullWidth
                                        placeholder="Search podcasts..."
                                        value={podcastSearchTerm}
                                        onChange={(e) => {
                                            setPodcastSearchTerm(e.target.value);
                                            setPodcastPage(1);
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Grid container spacing={2}>
                                        <Grid size={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Category</InputLabel>
                                                <Select
                                                    value={podcastCategoryFilter}
                                                    label="Category"
                                                    onChange={(e) => {
                                                        setPodcastCategoryFilter(e.target.value);
                                                        setPodcastPage(1);
                                                    }}
                                                >
                                                    <MenuItem value="">All Categories</MenuItem>
                                                    <MenuItem value="news">News</MenuItem>
                                                    <MenuItem value="entertainment">Entertainment</MenuItem>
                                                    <MenuItem value="technology">Technology</MenuItem>
                                                    <MenuItem value="business">Business</MenuItem>
                                                    <MenuItem value="sports">Sports</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={6}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Sort By</InputLabel>
                                                <Select
                                                    value={podcastSortBy}
                                                    label="Sort By"
                                                    onChange={(e) => {
                                                        setPodcastSortBy(e.target.value);
                                                        setPodcastPage(1);
                                                    }}
                                                >
                                                    <MenuItem value="">Default</MenuItem>
                                                    <MenuItem value="title">Title</MenuItem>
                                                    <MenuItem value="episodeCount">Episode Count</MenuItem>
                                                    <MenuItem value="lastUpdated">Last Updated</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Stack>

                                {/* Podcast List */}
                                {podcastsLoading ? (
                                    <Box display="flex" justifyContent="center" p={3}>
                                        <CircularProgress />
                                    </Box>
                                ) : podcastsError ? (
                                    <Alert severity="error">{podcastsError}</Alert>
                                ) : (
                                    <>
                                        <List>
                                            {podcasts.map((podcast, index) => (
                                                <React.Fragment key={podcast._id}>
                                                    <ListItem
                                                        onClick={() => handlePodcastSelect(podcast)}
                                                        sx={{
                                                            borderRadius: 1,
                                                            mb: 1,
                                                            cursor: 'pointer',
                                                            backgroundColor: selectedPodcast?._id === podcast._id ? 'primary.light' : 'transparent',
                                                            color: selectedPodcast?._id === podcast._id ? 'primary.contrastText' : 'inherit',
                                                            '&:hover': {
                                                                backgroundColor: selectedPodcast?._id === podcast._id ? 'primary.main' : 'action.hover',
                                                            },
                                                        }}
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar
                                                                src={podcast.imageUrl}
                                                                sx={{ bgcolor: 'primary.main' }}
                                                            >
                                                                <PodcastIcon />
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Typography variant="subtitle1">{podcast.title}</Typography>
                                                                    {podcast.type && (
                                                                        <Chip
                                                                            label={podcast.type}
                                                                            size="small"
                                                                            color="default"
                                                                        />
                                                                    )}
                                                                </Box>
                                                            }
                                                            secondary={
                                                                <Box>
                                                                    {podcast.author && (
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {podcast.author}
                                                                        </Typography>
                                                                    )}
                                                                    <Box display="flex" gap={1} mt={0.5}>
                                                                        {podcast.category && (
                                                                            <Chip label={podcast.category} size="small" variant="outlined" />
                                                                        )}
                                                                        {podcast.episodeCount && (
                                                                            <Chip label={`${podcast.episodeCount} episodes`} size="small" variant="outlined" />
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            }
                                                        />
                                                    </ListItem>
                                                    {index < podcasts.length - 1 && <Divider />}
                                                </React.Fragment>
                                            ))}
                                        </List>

                                        {/* Podcast Pagination */}
                                        {totalPodcastPages > 1 && (
                                            <Box display="flex" justifyContent="center" mt={2}>
                                                <Pagination
                                                    count={totalPodcastPages}
                                                    page={podcastPage}
                                                    onChange={(_, newPage) => setPodcastPage(newPage)}
                                                    color="primary"
                                                />
                                            </Box>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Panel - Podcast Details */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {selectedPodcast ? `${selectedPodcast.title} - Details` : 'Select a Podcast'}
                                </Typography>

                                {selectedPodcast ? (
                                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                        <Grid container spacing={2}>
                                            <Grid size={12}>
                                                <Box display="flex" alignItems="center" gap={2} mb={2}>
                                                    <Avatar
                                                        src={selectedPodcast.imageUrl}
                                                        sx={{ width: 80, height: 80 }}
                                                    >
                                                        <PodcastIcon sx={{ fontSize: 40 }} />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="h6">{selectedPodcast.title}</Typography>
                                                        {selectedPodcast.author && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                by {selectedPodcast.author}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>

                                                {selectedPodcast.description && (
                                                    <Typography variant="body2" paragraph>
                                                        {selectedPodcast.description}
                                                    </Typography>
                                                )}

                                                <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
                                                    {selectedPodcast.category && (
                                                        <Chip label={selectedPodcast.category} color="primary" size="small" />
                                                    )}
                                                    {selectedPodcast.type && (
                                                        <Chip
                                                            label={selectedPodcast.type}
                                                            color="default"
                                                            size="small"
                                                        />
                                                    )}
                                                    {selectedPodcast.language && (
                                                        <Chip label={selectedPodcast.language} variant="outlined" size="small" />
                                                    )}
                                                    {selectedPodcast.episodeCount && (
                                                        <Chip
                                                            label={`${selectedPodcast.episodeCount} episodes`}
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    )}
                                                </Box>

                                                {selectedPodcast.lastUpdated && (
                                                    <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                                                        Last updated: {formatChileanDate(selectedPodcast.lastUpdated)}
                                                    </Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                ) : (
                                    <Alert severity="info">
                                        Select a podcast from the left panel to view its details
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Mention Detail Dialog */}
            <Dialog
                open={mentionDialogOpen}
                onClose={() => setMentionDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        {selectedMention?.content && getSourceIcon(selectedMention.content.source)}
                        <Typography variant="h6">Brand Mention Details</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedMention && (
                        <Grid container spacing={3}>
                            <Grid size={12}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Content Information
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    <Typography variant="body1" fontWeight="bold">
                                        {selectedMention.content?.title || 'Unknown Content'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedMention.content?.podcast?.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Published: {selectedMention.content?.publishedAt && formatChileanDate(selectedMention.content.publishedAt)}
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid size={12}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Mention Details
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    <Grid container spacing={2}>
                                        <Grid size={6}>
                                            <Typography variant="body2" color="text.secondary">Brand</Typography>
                                            <Typography variant="body1">{selectedMention.brandName}</Typography>
                                        </Grid>
                                        <Grid size={6}>
                                            <Typography variant="body2" color="text.secondary">Classification</Typography>
                                            <Chip
                                                label={selectedMention.classification}
                                                size="small"
                                                color={getClassificationColor(selectedMention.classification) as any}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <Typography variant="body2" color="text.secondary">Time Range</Typography>
                                            <Typography variant="body1">
                                                {formatTime(selectedMention.startTime)} - {formatTime(selectedMention.endTime)}
                                            </Typography>
                                        </Grid>
                                        <Grid size={6}>
                                            <Typography variant="body2" color="text.secondary">Confidence</Typography>
                                            <Typography variant="body1">
                                                {(selectedMention.confidence * 100).toFixed(0)}%
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            <Grid size={12}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Context
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    <Typography variant="body2">
                                        {selectedMention.context}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    {selectedMention?.content?.url && (
                        <Button
                            startIcon={<PlayIcon />}
                            onClick={() => {
                                const url = createYouTubeUrlWithTimestamp(
                                    selectedMention.content!.url!,
                                    selectedMention.startTime
                                );
                                window.open(url, '_blank');
                            }}
                            color="primary"
                        >
                            Play in YouTube
                        </Button>
                    )}
                    <Button onClick={() => setMentionDialogOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default BrandExplorer;
